import {
  Alert,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  HairlineCard,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  StatusChip,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type {
  ClassificationStageData,
  ClerkReviewStatus,
  FileDetail,
  Form4FieldValues,
  Step5Data,
} from '@munifocus/contracts-gov';
import { EntryWarrantPanel } from '../../components/EntryWarrantPanel';
import { EscalationStepLayout } from '../../components/EscalationStepLayout';
import { Form4NameSection } from '../../components/clerk/ClerkApprovalsPanel';
import { Form4Fields } from '../../components/Form4Fields';
import { EmergencyBypassPanel } from '../../components/EmergencyBypassPanel';
import { LegalChecklist } from '../../components/LegalChecklist';
import { ServiceRecords } from '../../components/ServiceRecords';
import { useSaveStage } from '../../lib/files-api';
import { useForms, useGenerateNoticeToComply } from '../../lib/forms-api';
import { appealClock } from '../../lib/escalation-display';

/**
 * Type 3 sub-flow, Step 5 — Form 4: Notice to Comply (ticket E2). Form 4
 * pre-populates from the file record and is editable field by field at 5.1, with
 * every override logged (see `Form4Fields`); the assembled document comes from
 * the document engine (Epic G). The officer captures the clerk review,
 * seal/registration, and service details; serving the notice starts the 14-day
 * appeal clock.
 */
export function Step5Notice({ file }: { file: FileDetail }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const save = useSaveStage(file.id, 'step_5');
  const forms = useForms(file.id);
  const generate = useGenerateNoticeToComply(file.id);
  const initial = (file.stageData?.step_5 ?? {}) as Step5Data;
  const classification = (file.stageData?.classification ?? {}) as ClassificationStageData;

  const [noticeDetails, setNoticeDetails] = useState(initial.noticeDetails ?? '');
  const [clerkStatus, setClerkStatus] = useState<ClerkReviewStatus>(
    initial.clerkStatus ?? 'pending',
  );
  const [sealApplied, setSealApplied] = useState(initial.sealApplied ?? false);
  const [registered, setRegistered] = useState(initial.registered ?? false);
  const [registrationRef, setRegistrationRef] = useState(initial.registrationRef ?? '');
  const [sealDate, setSealDate] = useState(initial.sealDate?.slice(0, 10) ?? '');
  const [registryOffice, setRegistryOffice] = useState(initial.registryOffice ?? '');
  const [registrationDate, setRegistrationDate] = useState(
    initial.registrationDate?.slice(0, 10) ?? '',
  );
  const [registrationNumber, setRegistrationNumber] = useState(initial.registrationNumber ?? '');
  const [voluntaryInspection, setVoluntaryInspection] = useState(
    initial.voluntaryInspection ?? false,
  );
  const [accessRefused, setAccessRefused] = useState(initial.accessRefused ?? false);

  // The appeal clock runs off the operative service record (5.3), mirrored onto
  // step_5 by the service-records API — not off a field on this form.
  const servedDate = initial.serviceDate?.slice(0, 10) ?? '';
  const clock = servedDate ? appealClock(servedDate) : null;

  const onSave = () =>
    save.mutate(
      {
        noticeDetails: noticeDetails.trim() || undefined,
        clerkStatus,
        sealApplied,
        sealDate: sealDate || undefined,
        registered,
        registrationRef: registrationRef.trim() || undefined,
        registryOffice: registryOffice.trim() || undefined,
        registrationDate: registrationDate || undefined,
        registrationNumber: registrationNumber.trim() || undefined,
        // serviceMethod/serviceDate are owned by the 5.3 service records and
        // mirrored server-side; this form must not overwrite them.
        voluntaryInspection,
        accessRefused,
        advance: false,
      },
      { onSuccess: () => navigate(`/files/${file.id}`) },
    );

  // Assemble the Form 4 from the field sheet the officer confirmed at 5.1. The
  // sheet travels with the request so the API can diff it against the file's own
  // values and log any override against the notice it appeared on.
  const onGenerate = (fieldValues: Form4FieldValues) =>
    generate.mutate({
      violationSummary: noticeDetails.trim() || classification.correctiveWork || '',
      deadlineIso: classification.complianceDeadline,
      fieldValues,
    });

  const noticeForms = (forms.data ?? []).filter((f) => f.kind === 'notice-to-comply');

  return (
    <EscalationStepLayout
      file={file}
      step="step_5"
      onSave={onSave}
      saving={save.isPending}
      saveLabel={t('common.actions.save')}
    >
      {save.isError && <Alert severity="error">{t('gov.stage.saveError')}</Alert>}

      {file.emergency && <Alert severity="error">{t('gov.escalation.step5.emergencyNote')}</Alert>}

      {/* Sub-step 5.1 — the Form 4 field sheet: pre-populated from the file,
          editable field by field, every override logged. */}
      <Form4Fields
        fileId={file.id}
        forms={noticeForms}
        onGenerate={onGenerate}
        generating={generate.isPending}
        generateError={generate.isError}
      />

      {/* The Form 4 prints the municipality's officer: the Clerk approves their
          name on it before it is signed. */}
      <Form4NameSection
        fileId={file.id}
        formId={noticeForms.find((f) => f.status === 'published')?.id}
      />

      <TextField
        fullWidth
        multiline
        minRows={2}
        label={t('gov.escalation.step5.noticeDetails')}
        value={noticeDetails}
        onChange={(e) => setNoticeDetails(e.target.value)}
      />

      {/* Sub-step 5.1 — the digital legal checklist. Saves per item, so it is
          independent of this screen's Save button. */}
      {/* First of the bypass's two entry points. Declaring here keeps the
          Form 4 issuing but on the officer's compressed timeline. */}
      <EmergencyBypassPanel fileId={file.id} enteredFrom="step_5" />

      <LegalChecklist fileId={file.id} />

      <FormControl fullWidth size="small">
        <InputLabel>{t('gov.escalation.step5.clerkStatus')}</InputLabel>
        <Select
          label={t('gov.escalation.step5.clerkStatus')}
          aria-label={t('gov.escalation.step5.clerkStatus')}
          value={clerkStatus}
          onChange={(e) => setClerkStatus(e.target.value as ClerkReviewStatus)}
        >
          <MenuItem value="pending">{t('gov.escalation.step5.clerkPending')}</MenuItem>
          <MenuItem value="reviewed">{t('gov.escalation.step5.clerkReviewed')}</MenuItem>
        </Select>
      </FormControl>

      {/* Sub-step 5.2 — seal & registration particulars (s. 135 imputes notice
          to subsequent owners once registered). */}
      <Divider />
      <Typography variant="subtitle2">{t('gov.escalation.step5.sealRegistration')}</Typography>

      <FormControlLabel
        control={
          <Checkbox checked={sealApplied} onChange={(e) => setSealApplied(e.target.checked)} />
        }
        label={t('gov.escalation.step5.sealApplied')}
      />
      {sealApplied && (
        <TextField
          fullWidth
          size="small"
          type="date"
          label={t('gov.escalation.step5.sealDate')}
          slotProps={{ inputLabel: { shrink: true } }}
          value={sealDate}
          onChange={(e) => setSealDate(e.target.value)}
        />
      )}

      <FormControlLabel
        control={
          <Checkbox checked={registered} onChange={(e) => setRegistered(e.target.checked)} />
        }
        label={t('gov.escalation.step5.registered')}
      />
      {registered && (
        <>
          <TextField
            fullWidth
            size="small"
            label={t('gov.escalation.step5.registryOffice')}
            value={registryOffice}
            onChange={(e) => setRegistryOffice(e.target.value)}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t('gov.escalation.step5.registrationDate')}
              slotProps={{ inputLabel: { shrink: true } }}
              value={registrationDate}
              onChange={(e) => setRegistrationDate(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label={t('gov.escalation.step5.registrationNumber')}
              value={registrationNumber}
              onChange={(e) => setRegistrationNumber(e.target.value)}
            />
          </Stack>
          <TextField
            fullWidth
            size="small"
            label={t('gov.escalation.step5.registrationRef')}
            value={registrationRef}
            onChange={(e) => setRegistrationRef(e.target.value)}
          />
        </>
      )}

      <Divider />

      {/* Sub-step 5.3 — service records (repeatable, method-specific). */}
      <ServiceRecords fileId={file.id} />

      {/* 14-day appeal clock — prominent once the notice is served. */}
      {clock && (
        <HairlineCard sx={{ p: 2 }}>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Stack spacing={0.25}>
              <Typography variant="overline" color="text.secondary">
                {t('gov.escalation.step5.appealClock')}
              </Typography>
              <Typography variant="h6">
                {clock.expired
                  ? t('gov.escalation.step5.appealClosed')
                  : t('gov.escalation.step5.appealDaysLeft', { count: clock.daysRemaining })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('gov.escalation.step5.appealDeadline', { date: clock.deadlineIso })}
              </Typography>
            </Stack>
            <StatusChip
              status={clock.expired ? 'success' : 'warning'}
              label={
                clock.expired
                  ? t('gov.escalation.step5.appealFinal')
                  : t('gov.escalation.step5.appealOpen')
              }
            />
          </Stack>
          {clock.expired && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('gov.escalation.step5.appealFinalNote')}
            </Typography>
          )}
        </HairlineCard>
      )}

      <Divider />

      {/* Sub-step 5.5 — optional voluntary compliance inspection. */}
      <Typography variant="subtitle2">{t('gov.escalation.step5.voluntary')}</Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={voluntaryInspection}
            onChange={(e) => setVoluntaryInspection(e.target.checked)}
          />
        }
        label={t('gov.escalation.step5.voluntaryInspection')}
      />
      {voluntaryInspection && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={accessRefused}
                onChange={(e) => setAccessRefused(e.target.checked)}
              />
            }
            label={t('gov.escalation.step5.accessRefused')}
          />
          {accessRefused && (
            <Alert severity="info">{t('gov.escalation.step5.accessRefusedNote')}</Alert>
          )}
          {/* Sub-step 5.5 refusal → entry-warrant sub-flow (brief §6). */}
          {accessRefused && <EntryWarrantPanel fileId={file.id} triggerPhase="step-5" />}
        </>
      )}
    </EscalationStepLayout>
  );
}
